/**
 * /opt-out-keywords — Opt-out keyword list + CRUD (Story 7.9).
 *
 * Surface:
 *   - Paginated list with match_type badge + active toggle via edit.
 *   - "Nova keyword" button → modal create form with regex validity check.
 *   - Row actions: edit, deactivate (admin-only soft delete).
 *   - Toast feedback on every mutation.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Power } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import { ApiError, apiClient } from '@/lib/api';
import { formatDate } from '@/lib/format-date';
import {
  OptOutKeywordForm,
  type KeywordMatchType,
  type OptOutKeywordFormValue,
} from '@/components/opt-out/OptOutKeywordForm';

interface OptOutKeywordDto {
  id: string;
  keyword: string;
  matchType: KeywordMatchType;
  active: boolean;
  createdAt: string;
}

interface ListResponse {
  data: OptOutKeywordDto[];
  page: number;
  pageSize: number;
  total: number;
}

const MATCH_TYPE_BADGE: Record<KeywordMatchType, string> = {
  exact: 'bg-blue-500/20 text-blue-700 dark:text-blue-300',
  contains: 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300',
  regex: 'bg-purple-500/20 text-purple-700 dark:text-purple-300',
};

function MatchTypeBadge({ matchType }: { matchType: KeywordMatchType }) {
  return (
    <span
      data-testid={`match-type-${matchType}`}
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${MATCH_TYPE_BADGE[matchType]}`}
    >
      {matchType}
    </span>
  );
}

function humanizeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { code?: string; message?: string } };
    const code = body?.error?.code;
    if (code === 'KEYWORD_TAKEN') return 'Já existe uma keyword com esse texto.';
    if (code === 'KEYWORD_NOT_FOUND') return 'Keyword não encontrada.';
    if (code === 'VALIDATION_FAILED') return body.error?.message ?? 'Validação falhou.';
    if (body?.error?.message) return body.error.message;
    return `${fallback} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

function toFormValue(kw: OptOutKeywordDto): OptOutKeywordFormValue {
  return { keyword: kw.keyword, matchType: kw.matchType, active: kw.active };
}

export function OptOutKeywordsPage() {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<OptOutKeywordDto | null>(null);

  const listQuery = useQuery({
    queryKey: ['opt-out-keywords'],
    queryFn: () => apiClient.get<ListResponse>('/admin/opt-out-keywords?page=1&pageSize=100'),
  });

  const createMutation = useMutation({
    mutationFn: (body: OptOutKeywordFormValue) =>
      apiClient.post<OptOutKeywordDto>('/admin/opt-out-keywords', body),
    onSuccess: () => {
      toast.success('Keyword criada.');
      setCreateOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['opt-out-keywords'] });
    },
    onError: (err) => toast.error(humanizeError(err, 'Falha ao criar keyword.')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<OptOutKeywordFormValue> }) =>
      apiClient.put<OptOutKeywordDto>(`/admin/opt-out-keywords/${id}`, body),
    onSuccess: () => {
      toast.success('Keyword atualizada.');
      setEditTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['opt-out-keywords'] });
    },
    onError: (err) => toast.error(humanizeError(err, 'Falha ao atualizar keyword.')),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete<OptOutKeywordDto>(`/admin/opt-out-keywords/${id}`),
    onSuccess: () => {
      toast.success('Keyword desativada.');
      void queryClient.invalidateQueries({ queryKey: ['opt-out-keywords'] });
    },
    onError: (err) => toast.error(humanizeError(err, 'Falha ao desativar keyword.')),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Opt-out Keywords</h1>
          <p className="text-sm text-muted-foreground">
            Palavras-chave que disparam opt-out automático (Layer 1 do filtro de compliance).
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1" aria-hidden />
          Nova keyword
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Keywords cadastradas</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {listQuery.isLoading ? (
            <div className="p-6 text-sm text-muted-foreground">Carregando…</div>
          ) : listQuery.error ? (
            <div className="p-6 text-sm text-destructive">
              {humanizeError(listQuery.error, 'Falha ao carregar keywords.')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" role="table">
                <thead className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2">Keyword</th>
                    <th className="px-4 py-2">Tipo</th>
                    <th className="px-4 py-2">Ativa</th>
                    <th className="px-4 py-2">Criada</th>
                    <th className="px-4 py-2 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {listQuery.data && listQuery.data.data.length > 0 ? (
                    listQuery.data.data.map((k) => (
                      <tr key={k.id} className="border-b last:border-b-0">
                        <td className="px-4 py-2 font-mono text-xs">{k.keyword}</td>
                        <td className="px-4 py-2">
                          <MatchTypeBadge matchType={k.matchType} />
                        </td>
                        <td className="px-4 py-2">{k.active ? 'Sim' : 'Não'}</td>
                        <td className="px-4 py-2 text-muted-foreground">
                          {formatDate(k.createdAt)}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditTarget(k)}
                              aria-label={`Editar ${k.keyword}`}
                            >
                              <Pencil aria-hidden />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={!k.active || deactivateMutation.isPending}
                              onClick={() => deactivateMutation.mutate(k.id)}
                              aria-label={`Desativar ${k.keyword}`}
                            >
                              <Power aria-hidden />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                        Nenhuma keyword cadastrada.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova keyword</DialogTitle>
            <DialogDescription>
              Regex é case-insensitive e roda contra o texto original; exact/contains normalizam
              antes de comparar.
            </DialogDescription>
          </DialogHeader>
          <OptOutKeywordForm
            mode="create"
            submitting={createMutation.isPending}
            onCancel={() => setCreateOpen(false)}
            onSubmit={(next) => createMutation.mutate(next)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={editTarget !== null} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar keyword</DialogTitle>
          </DialogHeader>
          {editTarget ? (
            <OptOutKeywordForm
              mode="edit"
              submitting={updateMutation.isPending}
              initial={toFormValue(editTarget)}
              onCancel={() => setEditTarget(null)}
              onSubmit={(next) => updateMutation.mutate({ id: editTarget.id, body: next })}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default OptOutKeywordsPage;
